import {
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    Autocomplete,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead, 
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Avatar,
    Chip,
    Link,
    Typography,
    CircularProgress
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import { generateProjectsWithWorkersReport, getAllClients, getProjectsWithWorkers } from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import { format } from "date-fns";
import { People, Download } from "@mui/icons-material";
import { toast } from "react-toastify";

interface ProjectWorker {
    userId: string;
    fullName?: string;
    pts?: string;
    email?: string;
    phoneNumber?: string;
    postCode?: string;
    shifts: number;
    isWorking?: boolean;
}

interface ProjectRow {
    id: string;
    projectName?: string;
    client?: string;
    createdDate: string;
    startDate?: string;
    postCode?: string;
    status: boolean;
    workers: ProjectWorker[];
}

type StatusFilter = 'all' | 'active' | 'inactive';

const ProjectsOverview = () => {
    const { state } = useAuth();
    const [projects, setProjects] = useState<ProjectRow[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [clients, setClients] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const [workersProject, setWorkersProject] = useState<ProjectRow | null>(null);
    const [workerSearch, setWorkerSearch] = useState('');
    const [reportClient, setReportClient] = useState<any>(null);
    const [reportProject, setReportProject] = useState<any>(null);
    const [generatingReport, setGeneratingReport] = useState(false);

    const getClientsList = async () => {
        const response = await getAllClients(state.user?.token);
        if (response) {
            setClients(response);
        }
    };

    const getAllProjects = async () => {
        try {
            setLoading(true);
            const response = await getProjectsWithWorkers(state.user?.token, startDate, endDate);
            console.log("Projects response:", response);
            if (response) {
                setProjects(response.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch projects:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getClientsList();
    }, []);

    useEffect(() => {
        if (startDate && endDate && startDate > endDate) return;
        getAllProjects();
    }, [startDate, endDate]);

    const filteredProjects = useMemo(() => {
        return projects.filter((p) => {
            if (statusFilter === 'active' && !p.status) return false;
            if (statusFilter === 'inactive' && p.status) return false;
            if (reportProject) return p.id === reportProject.id;
            if (reportClient && p.client !== reportClient.companyName) return false;
            return true;
        });
    }, [projects, statusFilter, reportClient, reportProject]);

    const closeWorkers = () => {
        setWorkersProject(null);
        setWorkerSearch('');
    };

    const handleReportClientChange = (client: any) => {
        setReportClient(client);
        setReportProject(null);
    };

    const reportProjectOptions = useMemo(() => {
        if (!reportClient) return [];
        return projects.filter((p) => p.client === reportClient.companyName);
    }, [projects, reportClient]);

    const handleGenerateReport = async () => {
        setGeneratingReport(true);
        try {
            await generateProjectsWithWorkersReport(
                state.user?.token,
                reportProject?.id || undefined,
                startDate || undefined,
                endDate || undefined,
                reportClient?.id || undefined
            );
        } catch (error) {
            console.error("Failed to generate report:", error);
            toast.error('Failed to generate report');
        } finally {
            setGeneratingReport(false);
        }
    };

    const getInitials = (name?: string) =>
        (name || '?').split(' ').filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join('');

    const handleViewProfile = (userId: string) => {
        window.open(`/pace-team/resourcing/profile?workerId=${userId}`, '_blank');
    };

    const filteredWorkers = useMemo(() => {
        const term = workerSearch.trim().toLowerCase();
        const list = workersProject?.workers ?? [];
        if (!term) return list;
        return list.filter((w) =>
            [w.fullName, w.pts, w.email, w.phoneNumber, w.postCode].some((v) => v?.toLowerCase().includes(term))
        );
    }, [workersProject, workerSearch]);

    const columns: any = [
        {
            field: "projectName",
            headerName: "Project Name",
            flex: 0.4,
            renderCell: (params: any) => (
                <div>{params.row.projectName}</div>
            ),
        },
        {
            field: "companyId",
            headerName: "Client",
            flex: 0.4,
            renderCell: (params: any) => (
                <div>{params.row.client || 'N/A'}</div>
            ),
        },
        {
            field: "workers",
            headerName: "Workers",
            flex: 0.4,
            sortable: false,
             renderCell: (params: any) => (
                <Button
                    size="small"
                    variant="text"
                    startIcon={<People fontSize="small" />}
                    disabled={!params.row.workers?.length}
                    onClick={() => setWorkersProject(params.row)}
                    sx={{
                        textTransform: "none",
                        // fontWeight: 600,
                        justifyContent: "flex-start",
                        px: 1,
                        borderRadius: "8px",
                    }}
                >
                    {params.row.workers?.length ?? 0} {params.row.workers?.length === 1 ? 'Worker' : 'Workers'}
                </Button>
            ),
        },
        {
            field: "postCode",
            headerName: "Type of Work",
            flex: 0.25,
            renderCell: (params: any) => (
                <div>{params.row.postCode || 'N/A'}</div>
            ),
        },
        {
            field: "createdDate",
            headerName: "Created Date",
            flex: 0.25,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <div>
                    {format(new Date(params.row.createdDate || Date.now()), 'dd/MM/yyyy')}
                </div>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.25,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <Chip
                    size="small"
                    label={params.row.status ? 'Active' : 'Inactive'}
                    sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        height: 22,
                        color: params.row.status ? "#15803d" : "#b91c1c",
                        backgroundColor: params.row.status ? "#e8f7ee" : "#fdecec",
                        border: `1px solid ${params.row.status ? "#bbf0cc" : "#f7cfcf"}`,
                    }}
                />
            ),
        }
    ];

    return (
        <Box sx={{ p: 1 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: '16px',
                    backgroundColor: '#ffffff',
                    maxWidth: '1500px',
                    margin: '0 auto'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 3
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Autocomplete
                            size="small"
                            sx={{ minWidth: 204 }}
                            options={clients}
                            getOptionLabel={(c: any) => c.companyName || ''}
                            isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                            value={reportClient}
                            onChange={(_, value) => handleReportClientChange(value)}
                            renderInput={(params) => <TextField {...params} label="Client" placeholder="Search client" />}
                        />
                        <Autocomplete
                            size="small"
                            sx={{ minWidth: 220 }}
                            options={reportProjectOptions}
                            getOptionLabel={(p: any) => p.projectName || ''}
                            isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                            value={reportProject}
                            onChange={(_, value) => setReportProject(value)}
                            disabled={!reportClient}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Project"
                                    placeholder={reportClient ? "Search project" : "Select a client first"}
                                />
                            )}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="Start date"
                            value={startDate}
                            sx={{ minWidth: 180 }}
                            onChange={(e) => setStartDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: endDate || undefined } }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="End date"
                            value={endDate}
                            sx={{ minWidth: 180 }}
                            onChange={(e) => setEndDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: startDate || undefined } }}
                        />
                        {(startDate || endDate) && (
                            <Button size="small" color="inherit" onClick={() => { setStartDate(''); setEndDate(''); }}>
                                Clear dates
                            </Button>
                        )}
                        <Select
                            size="small"
                            value={statusFilter}
                            sx={{ minWidth: 180 }}
                            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                        >
                            <MenuItem value="all">All statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            disabled={generatingReport}
                            onClick={handleGenerateReport}
                            startIcon={generatingReport ? <CircularProgress size={16} color="inherit" /> : <Download fontSize="small" />}
                            style={{ boxShadow: 'none' }}
                        >
                            Generate Report
                        </Button>
                    </Box>
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        margin: '0 auto',
                        height: '730px',
                        "@media (max-width: 800px)": {
                            margin: 1,
                            minWidth: "95%",
                        },
                    }}>
                    <DataGrid
                        rows={filteredProjects}
                        columns={columns}
                        loading={loading}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 100 } },
                        }}
                        sx={{
                            "@media (max-width: 700px)": {
                                width: "100%",
                                overflowX: "scroll",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                color: "#333",
                                fontWeight: "bold",
                                fontSize: 15,
                                backgroundColor: '#f0f0f0',
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                            },
                            "@media (max-width: 600px)": {
                                fontSize: "0.7rem",
                            },
                        }}
                    />
                </Box>
            </Paper>

            <Dialog
                open={!!workersProject}
                onClose={closeWorkers}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{workersProject?.projectName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {workersProject?.client || 'N/A'} · {workersProject?.workers?.length ?? 0} workers
                        {workersProject?.startDate && (
                            <> · Started {format(new Date(workersProject.startDate), 'dd/MM/yyyy')}</>
                        )}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {workersProject?.workers?.length ? (
                        <>
                            <TextField
                                size="small"
                                placeholder="Search workers"
                                value={workerSearch}
                                onChange={(e) => setWorkerSearch(e.target.value)}
                                sx={{ mb: 2, width: 280 }}
                            />
                            <TableContainer sx={{ height: 570 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {['Worker', 'PTS', 'Email', 'Phone', 'Post Code'].map((h) => (
                                                <TableCell key={h} sx={{ fontWeight: 600, backgroundColor: '#f7f8fa' }}>{h}</TableCell>
                                            ))}
                                            <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: '#f7f8fa' }}>Shifts</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredWorkers.map((w) => {
                                            const notWorking = w.isWorking === false;
                                            return (
                                                <TableRow key={w.userId} hover sx={notWorking ? { opacity: 0.5 } : undefined}>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Avatar sx={{
                                                                width: 32,
                                                                height: 32,
                                                                fontSize: 13,
                                                                bgcolor: notWorking ? '#e0e0e0' : '#e3ecfa',
                                                                color: notWorking ? '#757575' : '#1a4fa0',
                                                            }}>
                                                                {getInitials(w.fullName)}
                                                            </Avatar>
                                                            {w.fullName ? (
                                                                <Link
                                                                    component="button"
                                                                    type="button"
                                                                    underline="hover"
                                                                    onClick={() => handleViewProfile(w.userId)}
                                                                    sx={{ fontWeight: 500, fontSize: '0.875rem', textAlign: 'left' }}
                                                                >
                                                                    {w.fullName}
                                                                </Link>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>-</Typography>
                                                            )}
                                                            {notWorking && (
                                                                <Chip size="small" label="Not working" variant="outlined" sx={{ color: 'text.secondary' }} />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{w.pts || '-'}</TableCell>
                                                    <TableCell>
                                                        {w.email || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {w.phoneNumber || '-'}
                                                    </TableCell>
                                                    <TableCell>{w.postCode || '-'}</TableCell>
                                                    <TableCell align="right">
                                                        <Chip size="small" label={w.shifts} />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {!filteredWorkers.length && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                                    No workers match your search.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : (
                        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            No workers on this project yet.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeWorkers} color="inherit">Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProjectsOverview;
